import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { 
  Building2, 
  Plus, 
  Edit, 
  Trash2, 
  Eye, 
  EyeOff,
  Link,
  Image as ImageIcon,
  ArrowUpDown
} from 'lucide-react';
import { API_URLS } from '../../../config/api';

const AdminReferences = () => {
  const [references, setReferences] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingReference, setEditingReference] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    logo: '',
    website: '',
    description: '',
    category_id: null,
    display_order: 0,
    is_active: 1
  });

  useEffect(() => {
    fetchReferences();
    fetchCategories();
  }, []);

  // ESC tuşu ile modal kapatma
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape' && showForm) {
        resetForm();
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [showForm]);

  const fetchReferences = async () => {
    try {
      const response = await fetch(API_URLS.ADMIN.REFERENCES);
      const result = await response.json();
      if (result.status === 'success') {
        setReferences(result.data || []);
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching references:', error);
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${API_URLS.ADMIN.REFERENCES.replace('getReferences', 'getReferenceCategories')}`);
      const result = await response.json();
      if (result.status === 'success') {
        setCategories(result.data || []);
      }
    } catch (error) {
      console.error('Error fetching reference categories:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const url = editingReference 
        ? API_URLS.ADMIN.UPDATE_REFERENCE 
        : API_URLS.ADMIN.CREATE_REFERENCE;

      const data = editingReference 
        ? { ...formData, id: editingReference.id }
        : formData;

      const response = await fetch(url, {
        method: editingReference ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });

      const result = await response.json();
      if (result.status === 'success') {
        fetchReferences();
        resetForm();
      }
    } catch (error) {
      console.error('Error saving reference:', error);
    }
  };

  const handleEdit = (reference) => {
    setEditingReference(reference);
    setFormData({
      name: reference.name,
      slug: reference.slug || '',
      logo: reference.logo,
      website: reference.website,
      description: reference.description || '',
      category_id: reference.category_id || null,
      display_order: reference.display_order,
      is_active: reference.is_active
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu referansı silmek istediğinizden emin misiniz?')) return;
    
    try {
      console.log('Deleting reference with ID:', id);
      const url = `${API_URLS.ADMIN.DELETE_REFERENCE}&id=${id}`;
      console.log('Delete URL:', url);
      
      const response = await fetch(url, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      console.log('Delete response status:', response.status);
      const result = await response.json();
      console.log('Delete result:', result);
      
      if (result.status === 'success') {
        alert('Referans başarıyla silindi!');
        fetchReferences();
      } else {
        alert('Hata: ' + (result.message || 'Referans silinemedi'));
      }
    } catch (error) {
      console.error('Error deleting reference:', error);
      alert('Referans silinirken bir hata oluştu: ' + error.message);
    }
  };

  const toggleStatus = async (id) => {
    try {
      const response = await fetch(API_URLS.ADMIN.TOGGLE_REFERENCE_STATUS(id), {
        method: 'PUT'
      });
      const result = await response.json();
      if (result.status === 'success') {
        fetchReferences();
      }
    } catch (error) {
      console.error('Error toggling status:', error);
    }
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingReference(null);
    setFormData({
      name: '',
      slug: '',
      logo: '',
      website: '',
      description: '',
      category_id: null,
      display_order: 0,
      is_active: 1
    });
  };

  // Auto-generate slug from name
  const generateSlug = (name) => {
    return name
      .toLowerCase()
      .replace(/ğ/g, 'g')
      .replace(/ü/g, 'u')
      .replace(/ş/g, 's')
      .replace(/ı/g, 'i')
      .replace(/ö/g, 'o')
      .replace(/ç/g, 'c')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  const handleNameChange = (name) => {
    setFormData({
      ...formData,
      name,
      slug: generateSlug(name)
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Referanslar yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 sm:p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Referanslar</h1>
          <p className="text-gray-600 mt-2">
            Anasayfada görünen marka referanslarını yönetin
          </p>
        </div>
        <Button
          onClick={() => setShowForm(true)}
          className="bg-blue-600 hover:bg-blue-700 text-white"
        >
          <Plus className="w-4 h-4 mr-2" />
          Yeni Referans
        </Button>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div 
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4 transition-opacity duration-300"
          onClick={resetForm}
        >
          <Card 
            className="border-blue-200 bg-white max-w-3xl w-full max-h-[90vh] overflow-y-auto transform transition-all duration-300 scale-100"
            onClick={(e) => e.stopPropagation()}
          >
            <CardHeader className="bg-gradient-to-r from-blue-600 to-purple-600 text-white">
              <div className="flex items-center justify-between">
                <CardTitle>
                  {editingReference ? 'Referans Düzenle' : 'Yeni Referans Ekle'}
                </CardTitle>
                <button
                  type="button"
                  onClick={resetForm}
                  className="text-white hover:bg-white/20 rounded-full p-2 transition-colors"
                >
                  ✕
                </button>
              </div>
            </CardHeader>
            <CardContent className="p-6">
              <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Firma Adı *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleNameChange(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Slug *
                  </label>
                  <input
                    type="text"
                    value={formData.slug}
                    onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="ornek-firma"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">URL: /referanslar/{formData.slug}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Website URL
                  </label>
                  <input
                    type="url"
                    value={formData.website}
                    onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="https://example.com"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Logo URL *
                  </label>
                  <input
                    type="url"
                    value={formData.logo}
                    onChange={(e) => setFormData({ ...formData, logo: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="https://example.com/logo.png"
                    required
                  />
                  {formData.logo && (
                    <div className="mt-2 p-2 bg-white rounded border">
                      <img 
                        src={formData.logo} 
                        alt="Preview" 
                        className="h-16 object-contain"
                        onError={(e) => e.target.style.display = 'none'}
                      />
                    </div>
                  )}
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Açıklama
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    rows="3"
                    placeholder="Firma hakkında kısa açıklama..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Kategori
                  </label>
                  <select
                    value={formData.category_id || ''}
                    onChange={(e) => setFormData({ ...formData, category_id: e.target.value ? parseInt(e.target.value) : null })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Kategori Seçin</option>
                    {categories.map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Sıralama
                  </label>
                  <input
                    type="number"
                    value={formData.display_order}
                    onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) || 0 })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    min="0"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Durum
                  </label>
                  <select
                    value={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: parseInt(e.target.value) })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value={1}>Aktif</option>
                    <option value={0}>Pasif</option>
                  </select>
                </div>
              </div>

                <div className="flex items-center space-x-4 pt-4">
                  <Button type="submit" className="bg-blue-600 hover:bg-blue-700 text-white">
                    {editingReference ? 'Güncelle' : 'Ekle'}
                  </Button>
                  <Button
                    type="button"
                    onClick={resetForm}
                    variant="outline"
                  >
                    İptal
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Toplam Referans</p>
                <p className="text-2xl font-bold text-gray-900">{references.length}</p>
              </div>
              <Building2 className="w-8 h-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Aktif</p>
                <p className="text-2xl font-bold text-green-600">
                  {references.filter(r => r.is_active === 1).length}
                </p>
              </div>
              <Eye className="w-8 h-8 text-green-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Pasif</p>
                <p className="text-2xl font-bold text-red-600">
                  {references.filter(r => r.is_active === 0).length}
                </p>
              </div>
              <EyeOff className="w-8 h-8 text-red-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* References List */}
      {references.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center">
            <Building2 className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Henüz referans yok</h3>
            <p className="text-gray-600 mb-4">İlk referansınızı ekleyerek başlayın.</p>
            <Button
              onClick={() => setShowForm(true)}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              <Plus className="w-4 h-4 mr-2" />
              Yeni Referans Ekle
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {references.map((reference) => (
            <Card key={reference.id} className="hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                {/* Logo Preview */}
                <div className="bg-gray-50 rounded-lg p-4 mb-4 h-32 flex items-center justify-center">
                  <img
                    src={reference.logo}
                    alt={reference.name}
                    className="max-h-full max-w-full object-contain grayscale hover:grayscale-0 transition-all"
                    onError={(e) => {
                      e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="100"%3E%3Crect width="200" height="100" fill="%23f3f4f6"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" font-family="Arial" font-size="14" fill="%236b7280"%3ENo Image%3C/text%3E%3C/svg%3E';
                    }}
                  />
                </div>

                {/* Info */}
                <div className="space-y-2">
                  <div className="flex items-start justify-between">
                    <h3 className="font-semibold text-gray-900 text-lg">{reference.name}</h3>
                    <Badge className={reference.is_active ? 'bg-green-500' : 'bg-red-500'}>
                      {reference.is_active ? 'Aktif' : 'Pasif'}
                    </Badge>
                  </div>

                  {reference.website && (
                    <div className="flex items-center text-sm text-gray-600">
                      <Link className="w-4 h-4 mr-2" />
                      <a 
                        href={reference.website} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="hover:text-blue-600 truncate"
                      >
                        {reference.website}
                      </a>
                    </div>
                  )}

                  {reference.description && (
                    <p className="text-sm text-gray-600 line-clamp-2">
                      {reference.description}
                    </p>
                  )}

                  <div className="flex items-center text-sm text-gray-500">
                    <ArrowUpDown className="w-4 h-4 mr-2" />
                    Sıra: {reference.display_order}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center space-x-2 mt-4 pt-4 border-t">
                  <Button
                    onClick={() => toggleStatus(reference.id)}
                    variant="outline"
                    size="sm"
                    className="flex-1"
                  >
                    {reference.is_active ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </Button>
                  <Button
                    onClick={() => handleEdit(reference)}
                    variant="outline"
                    size="sm"
                    className="flex-1"
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button
                    onClick={() => {
                      console.log('Delete button clicked for reference:', reference);
                      console.log('Reference ID:', reference.id);
                      handleDelete(reference.id);
                    }}
                    variant="outline"
                    size="sm"
                    className="flex-1 text-red-600 hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminReferences;
